<template>
  <div class="animate-fade-in">
    <div class="bg-white rounded-[2.5rem] shadow-xl overflow-hidden border border-gray-100">
      
      <div class="p-6 border-b bg-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
        
        <div class="flex items-center gap-4 w-full md:w-auto">
          <h2 class="text-2xl font-black text-carpatina-gri uppercase flex items-center gap-3">
            <ShoppingCart class="text-carpatina-portocaliu" :size="28" /> 
            Cereri Ofertă
          </h2>
          <span class="bg-carpatina-verde text-white px-3 py-1 rounded-full text-xs font-bold shadow-sm">
            {{ filteredCereri.length }}
          </span>
        </div>

        <div class="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          
          <div class="flex bg-white p-1 rounded-xl shadow-sm border border-gray-200">
            <button @click="filterStatus = 'all'" 
                    :class="filterStatus === 'all' ? 'bg-carpatina-gri text-white shadow-md' : 'text-gray-400 hover:text-carpatina-gri'"
                    class="px-4 py-2 rounded-lg text-xs font-black uppercase transition-all">
              Toate
            </button>
            <button @click="filterStatus = 'unread'" 
                    :class="filterStatus === 'unread' ? 'bg-carpatina-portocaliu text-white shadow-md' : 'text-gray-400 hover:text-carpatina-portocaliu'"
                    class="px-4 py-2 rounded-lg text-xs font-black uppercase transition-all flex items-center gap-1">
              Noi <span v-if="unreadCount > 0" class="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            </button>
            <button @click="filterStatus = 'read'" 
                    :class="filterStatus === 'read' ? 'bg-green-600 text-white shadow-md' : 'text-gray-400 hover:text-green-600'"
                    class="px-4 py-2 rounded-lg text-xs font-black uppercase transition-all">
              Arhivă
            </button>
          </div>

          <div class="relative flex-grow md:flex-grow-0 md:w-64">
            <input v-model="searchQuery" type="text" placeholder="Caută client / oraș..." 
                   class="w-full pl-10 pr-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-carpatina-portocaliu outline-none text-sm font-bold transition">
            <Search :size="18" class="absolute left-3 top-3 text-gray-400" />
          </div>

        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-carpatina-gri text-white text-xs uppercase tracking-widest">
              <th class="p-4">Data</th>
              <th class="p-4">Client</th>
              <th class="p-4">Contact & Locație</th>
              <th class="p-4 w-1/3">Necesar Materiale</th>
              <th class="p-4 text-center">Acțiuni</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            
            <tr v-for="c in filteredCereri" :key="c.id" 
                :class="!c.citita ? 'bg-orange-50/50' : 'hover:bg-gray-50'"
                class="transition-colors group align-top">
              
              <td class="p-4 text-xs font-bold text-gray-400 whitespace-nowrap pt-5">
                {{ formatData(c.data_creare) }}
              </td>

              <td class="p-4 pt-5">
                <div class="flex items-center gap-2">
                  <div :class="!c.citita ? 'bg-carpatina-portocaliu text-white' : 'bg-gray-100 text-gray-400'"
                       class="p-2 rounded-lg transition-colors">
                    <User :size="16" />
                  </div>
                  <span class="font-black text-carpatina-gri text-sm">{{ c.nume_client }}</span>
                  <span v-if="!c.citita" class="text-[10px] bg-red-500 text-white px-2 rounded-full font-bold uppercase">Nou</span>
                </div>
              </td>

              <td class="p-4 pt-5">
                <div class="space-y-1">
                  <a :href="'tel:' + c.telefon" class="flex items-center gap-1 text-sm font-bold text-carpatina-portocaliu hover:underline">
                    <Phone :size="14" /> {{ c.telefon }}
                  </a>
                  <div class="flex items-center gap-1 text-xs text-gray-500 font-bold">
                    <MapPin :size="12" /> {{ c.localitate }}
                  </div>
                  <div v-if="c.email" class="flex items-center gap-1 text-xs text-gray-500 italic mt-1">
                    <AtSign :size="12" /> {{ c.email }}
                  </div>
                </div>
              </td>

              <td class="p-4 pt-5">
                <div class="space-y-3">
                  <div class="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
                    <ul class="space-y-1.5">
                      <li v-for="item in c.produse_cerute" :key="item.id" class="flex justify-between items-start text-xs font-bold gap-4">
                        <span class="text-gray-600 line-clamp-1" :title="item.nume_produs_salvat">{{ item.nume_produs_salvat }}</span>
                        <span class="text-carpatina-portocaliu whitespace-nowrap bg-orange-50 px-1.5 rounded">{{ item.cantitate }} buc</span>
                      </li>
                    </ul>
                  </div>

                  <div v-if="c.mentiuni" class="text-xs text-gray-500 italic bg-gray-100 p-2.5 rounded-xl border-l-2 border-carpatina-portocaliu">
                    <span class="font-bold uppercase text-[10px] block mb-1 text-gray-400">Mențiuni Client:</span>
                    "{{ c.mentiuni }}"
                  </div>
                </div>
              </td>

              <td class="p-4 text-center pt-5">
                <div class="flex justify-center gap-2">
                  <button @click="marcheazaCitit(c)" 
                          v-if="!c.citita"
                          title="Marchează ca Rezolvată"
                          class="p-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-500 hover:text-white transition shadow-sm border border-green-100">
                    <CheckCircle :size="18" />
                  </button>
                  <button @click="stergeCerere(c.id)" 
                          title="Șterge Cerere"
                          class="p-2 bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition shadow-sm border border-red-100">
                    <Trash2 :size="18" />
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="filteredCereri.length === 0">
              <td colspan="5" class="p-20 text-center">
                <div class="flex flex-col items-center opacity-30 text-gray-400">
                  <PackageSearch :size="64" />
                  <p class="text-xl font-bold uppercase mt-4">Nu am găsit cereri</p>
                  <button @click="resetFilters" class="text-xs text-carpatina-portocaliu font-bold mt-2 hover:underline">
                    Șterge filtrele
                  </button>
                </div>
              </td>
            </tr>

          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  ShoppingCart, Phone, User, AtSign, Trash2, CheckCircle, 
  Search, MapPin, PackageSearch 
} from 'lucide-vue-next';

// STATE
const cereri = ref([]);
const searchQuery = ref('');
const filterStatus = ref('all'); // 'all', 'unread', 'read'

// LOGICĂ FILTRARE
const filteredCereri = computed(() => {
  return cereri.value.filter(c => {
    // 1. Filtru Text (Nume, Telefon, Localitate, Email)
    const query = searchQuery.value.toLowerCase();
    const matchSearch = !query || 
                        c.nume_client.toLowerCase().includes(query) || 
                        c.telefon.includes(query) || 
                        c.localitate.toLowerCase().includes(query) ||
                        (c.email && c.email.toLowerCase().includes(query));

    // 2. Filtru Status (Atenție: la cereri am folosit `citita`, la contact era `citit`)
    let matchStatus = true;
    if (filterStatus.value === 'unread') matchStatus = !c.citita;
    if (filterStatus.value === 'read') matchStatus = c.citita;

    return matchSearch && matchStatus;
  });
});

const unreadCount = computed(() => cereri.value.filter(c => !c.citita).length);

const fetchCereri = async () => {
  try {
    const res = await axios.get('/admin/cereri-oferta', {
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
    });
    cereri.value = res.data;
  } catch (err) {
    console.error("Eroare la incarcare cereri", err);
  }
};

const formatData = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ro-RO', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit', 
    minute: '2-digit'
  });
};

const marcheazaCitit = async (cerere) => {
  try {
    await axios.patch(`/admin/cerere-oferta/${cerere.id}/citita`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
    });
    cerere.citita = true; // Update local
  } catch (err) {
    Swal.fire('Eroare', 'Nu s-a putut actualiza statusul', 'error');
  }
};

const stergeCerere = async (id) => {
  const result = await Swal.fire({
    title: 'Ștergi cererea?',
    text: "Datele și materialele selectate se vor pierde!",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    confirmButtonText: 'Da, șterge!',
    cancelButtonText: 'Nu'
  });

  if (result.isConfirmed) {
    try {
      await axios.delete(`/admin/cerere-oferta/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      cereri.value = cereri.value.filter(c => c.id !== id);
      Swal.fire({ title: 'Ștearsă!', icon: 'success', timer: 1000, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Eroare', 'Nu s-a putut șterge cererea', 'error');
    }
  }
};

const resetFilters = () => {
    searchQuery.value = '';
    filterStatus.value = 'all';
}

onMounted(fetchCereri);
</script>

<style scoped>
.animate-fade-in { animation: fadeIn 0.4s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>